import ChatIcon from '@material-ui/icons/Chat';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import React, { useContext } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import styled, { css } from 'styled-components/macro';
import { iconButtonStyles } from './Button';
import { ThemeContext } from '../../../contexts/Theme';
import {
  focusRing,
  fontSize,
  fontWeight,
  layout,
  media,
  neutral,
  radius,
  shadow,
  space,
} from '../../../theme/tokens';

const AVATAR_SIZE = '3.2rem';
const AVATAR_SIZE_WIDE = '4rem';

// a single column timeline, oldest first, joined by a line through the avatars
export const EvidenceList = styled.ol`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${space[4]};
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;

  &::before {
    content: '';
    position: absolute;
    top: ${space[2]};
    bottom: ${space[2]};
    left: calc(${AVATAR_SIZE} / 2 - 1px);
    width: 2px;
    background: ${neutral.border};

    ${media.sm`
      left: calc(${AVATAR_SIZE_WIDE} / 2 - 1px);
    `}
  }
`;

export const TimelineEntry = styled.li`
  position: relative;
  display: grid;
  grid-template-columns: ${AVATAR_SIZE} minmax(0, 1fr);
  gap: ${space[2]};
  align-items: start;

  ${media.sm`
    grid-template-columns: ${AVATAR_SIZE_WIDE} minmax(0, 1fr);
    gap: ${space[3]};
  `}
`;

const avatarStyles = css`
  flex-shrink: 0;
  width: ${({ $small }) => ($small ? '2.4rem' : AVATAR_SIZE)};
  height: ${({ $small }) => ($small ? '2.4rem' : AVATAR_SIZE)};
  border-radius: 50%;
  /* a ring in the page colour so the timeline line stops at the avatar */
  box-shadow: 0 0 0 ${({ $small }) => ($small ? '0' : '4px')} ${neutral.surface};

  ${({ $small }) =>
    !$small &&
    media.sm`
      width: ${AVATAR_SIZE_WIDE};
      height: ${AVATAR_SIZE_WIDE};
    `}
`;

const AvatarImage = styled.img`
  ${avatarStyles}
  object-fit: cover;
  background: ${neutral.subtle};
`;

const AvatarInitial = styled.span`
  ${avatarStyles}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${neutral.subtle};
  font-size: ${({ $small }) => ($small ? fontSize.xs : fontSize.sm)};
  font-weight: ${fontWeight.bold};
  color: ${neutral.textMuted};
`;

export const TimelineAvatar = ({ user, ...props }) =>
  user?.imageURL ? (
    <AvatarImage src={user.imageURL} alt="" {...props} />
  ) : (
    <AvatarInitial {...props}>
      {user?.displayName?.[0]?.toUpperCase() || '?'}
    </AvatarInitial>
  );

export const EvidenceCommentContainer = styled.div`
  min-width: 0;
  border: 1px solid ${neutral.border};
  border-radius: ${radius.lg};
  padding: ${space[2]} ${space[3]} ${space[3]};
  background: ${neutral.surface};
  box-shadow: ${shadow.sm};
`;

export const EntryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space[2]};
  min-height: 3.6rem;
`;

export const EntryMeta = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[1]};
  margin: 0;
  font-size: ${fontSize.sm};
  color: ${neutral.textMuted};
`;

export const EntryAuthor = styled.span`
  font-weight: ${fontWeight.bold};
  color: ${({ theme }) => theme.headerColour};
`;

export const EvidenceItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};

  ${({ isAudio }) =>
    isAudio
      ? css`
          min-width: 18em;
          ${EvidenceContent} {
            border-radius: 2.5em 2.5em 2.5em 2.5em;
          }
        `
      : css`
          min-width: 10em;
        `}
`;

export const EvidenceContent = styled.div`
  margin: 0;
  background: ${({ theme }) => theme.userMessage};
  color: ${({ theme }) => theme.textLight};
  border-radius: ${radius.lg};
  line-height: 0;
  padding: 0.2em;
  display: flex;
  flex-direction: column;
  max-width: 40em;

  ${({ resource, theme }) =>
    resource === 'task' &&
    css`
      background: ${theme.completedFill};
      color: ${theme.isTaskDark ? theme.textLight : theme.headerColour};
    `}

  ${({ resource, theme }) =>
    resource === 'target' &&
    css`
      background: ${theme.targetHighlightPale};
      color: ${theme.isTargetDark ? theme.textLight : theme.headerColour};

      ${!theme.isTargetDark &&
      css`
        border: 2px solid ${theme.classroomHighlight};
      `};
    `}
`;

export const EvidenceText = styled.p`
  white-space: pre-line;
  margin: 0;
  padding: 0.2em 0.75em 0 0.75em;
  font-weight: 500;
  font-size: 1.6rem;
  line-height: 1.7em;
`;

export const EvidenceImage = styled.img`
  max-width: 100%;
  border-radius: 0.8em;
  align-self: center;
`;

export const EvidenceVideo = styled.video`
  max-width: 100%;
  border-radius: 0.8em;
`;

export const EvidenceAudio = styled(AudioPlayer)`
  min-width: 18em;
  padding: 0.3em 0.5em;
  border-radius: 2.8em 2.8em 2.8em 2.8em;
`;

export const EvidenceCreatedAt = styled.span``;

export const CommentItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
  margin-top: ${space[3]};
  border-top: 1px solid ${neutral.border};
  padding-top: ${space[2]};
`;

export const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space[2]};
  min-height: 3.6rem;
`;

export const CommentActions = styled.div`
  display: flex;
  align-items: center;
`;

export const AddCommentButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  align-self: flex-start;
  min-height: ${layout.touchTarget};
  padding: 0 ${space[3]};
  border: 1px dashed ${neutral.borderStrong};
  border-radius: ${radius.md};
  background: none;
  font-family: inherit;
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.bold};
  color: ${({ theme }) => theme.headerColour};
  cursor: pointer;

  &:hover {
    background: ${neutral.subtle};
  }
  ${focusRing}
`;

export const SpaceBetweenFlex = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${space[2]};
`;

export const SpaceBetweenFlexReverse = styled(SpaceBetweenFlex)`
  flex-direction: row-reverse;
`;

export const DeleteCommentButton = styled.button.attrs({ $tone: 'danger' })`
  ${iconButtonStyles}
`;

export const EditCommentButton = styled.button`
  ${iconButtonStyles}
`;

export const AddCommentIcon = (props) => {
  const theme = useContext(ThemeContext);
  return (
    <ChatIcon
      style={{
        fontSize: '1.8rem',
        color: theme.subHeaderColour,
      }}
      {...props}
    />
  );
};

export const EditCommentIcon = (props) => {
  const theme = useContext(ThemeContext);
  return (
    <EditOutlinedIcon
      style={{
        fontSize: '1.8rem',
        color: theme.subHeaderColour,
      }}
      {...props}
    />
  );
};

export const CommentContent = styled(EvidenceContent)`
  flex-direction: row;
  justify-content: space-between;
  background: ${({ theme }) => theme.feedbackLight};
  color: ${({ theme }) => theme.textDark};
  margin: 0;
  padding: ${space[2]} ${space[3]};
  border: 1px solid ${neutral.border};
  border-radius: ${radius.md};
  line-height: 1.5;
  ${({ withFile }) =>
    withFile &&
    css`
      display: grid;
      grid-template-columns: 90% 10%;
      grid-template-areas:
        'comment-text comment-edit'
        'comment-file comment-file';
    `}
`;

export const CommentText = styled(EvidenceText)`
  grid-area: comment-text;
  white-space: pre-line;
  padding: 0;
  font-weight: ${fontWeight.medium};
  font-size: ${fontSize.md};
  line-height: 1.5;
  color: ${({ theme }) => theme.headerColour};
`;

export const CommentImage = styled.img`
  grid-area: comment-file;
  max-width: 100%;
  border-radius: 0.8em;
`;

export const CommentVideo = styled.video`
  grid-area: comment-file;
  max-width: 100%;
  border-radius: 0.8em;
`;

export const CommentAudio = styled(AudioPlayer)`
  grid-area: comment-file;
  min-width: 18em;
  padding: 0.3em 0.5em;
  border-radius: 2.8em 2.8em 2.8em 2.8em;
`;

export * from './Staff';
export * from './Students';
